const UseFormLayoutUtils = require('./layout');
const UseFormGroupUtils = require('./group');
const UseFormComponentRegistry = require('./componentRegistry');
const UseFormCoreUtils = require('./core');
const UseFormStateUtils = require('./state');
const FieldTypeUtils = require('./fieldTypes');

const UseFormConfigUtils = module.exports = {};
const internals = {};

internals.groupComponentPropsKeys = [
    'columnGap',
    'columns',
    'description',
    'gap',
    'rowGap',
    'singleRowMinHeight',
    'title'
];

internals.formFieldExcludedKeys = new Set([
    'buildSignupProgressPayload',
    'componentKey',
    'getManagedDefault',
    'gridColumn',
    'gridSx',
    'group',
    'normalizeValue',
    'onChangeEffects',
    'ownerFieldName',
    'progressFieldName',
    'render',
    'trackProfileProgress',
    'uiStateDefaults',
    'visibleWhen'
]);

internals.buildGroupConfigComponentProps = (groupConfig = {}) => internals.groupComponentPropsKeys.reduce((collector, key) => {

    if (typeof groupConfig[key] !== 'undefined') {
        collector[key] = groupConfig[key];
    }

    return collector;
}, {});

UseFormConfigUtils.defaultRef = ({
    fieldName,
    path
}) => ({
    kind: 'default',
    fieldName,
    path,
    __useFormConfigRef: true
});

UseFormConfigUtils.ref = (fieldName, path) => UseFormConfigUtils.defaultRef({
    fieldName,
    path
});

UseFormConfigUtils.defineConfig = (configOrBuilder) => {

    if (typeof configOrBuilder !== 'function') {
        return configOrBuilder;
    }

    return configOrBuilder({
        defaultRef: UseFormConfigUtils.defaultRef,
        ref: UseFormConfigUtils.ref
    });
};

UseFormConfigUtils.buildForm = (configOrBuilder) => UseFormConfigUtils.finalizeConfig(
    UseFormConfigUtils.defineConfig(configOrBuilder)
);

UseFormConfigUtils.isRef = (value) => !!value?.__useFormConfigRef;

UseFormConfigUtils.toFieldDefinition = ({
    field = {},
    groupName
}) => {
    const {
        render,
        visibleWhen,
        ...definition
    } = field;

    return {
        ...definition,
        type: FieldTypeUtils.normalizePrimitiveType(definition.type),
        group: definition.group || groupName
    };
};

UseFormConfigUtils.buildComponentsByName = ({
    definitions = {},
    componentsByName = {}
} = {}) => {

    return Object.values(definitions).reduce((collector, definition) => {

        if (typeof definition.component === 'string' && componentsByName[definition.component]) {
            collector[definition.component] = componentsByName[definition.component];
        }

        return collector;
    }, {
        ...componentsByName
    });
};

UseFormConfigUtils.getNamedRegistryComponents = ({
    componentRegistry = {}
} = {}) => {
    const {
        componentsByName = {},
        defaultComponent,
        genericComponentsByType,
        genericFieldAdapter,
        fieldAdapter,
        ...namedComponents
    } = componentRegistry;

    return {
        ...namedComponents,
        ...componentsByName
    };
};

UseFormConfigUtils.buildComponentsByType = ({
    definitions = {},
    componentsByName = {},
    genericComponentsByType = {}
} = {}) => {

    return Object.values(definitions).reduce((collector, definition) => {

        const Component = typeof definition.component === 'string'
            ? componentsByName[definition.component]
            : null;
        const componentType = FieldTypeUtils.normalizePrimitiveType(definition.componentType || definition.type);

        if (Component && componentType && !genericComponentsByType[componentType]) {
            collector[componentType] = Component;
        }

        return collector;
    }, {});
};

UseFormConfigUtils.buildComponentsByKey = ({
    definitions = {},
    componentsByName = {}
} = {}) => {

    return Object.values(definitions).reduce((collector, definition) => {

        const Component = typeof definition.component === 'string'
            ? componentsByName[definition.component]
            : null;

        if (Component && definition.componentKey) {
            collector[definition.componentKey] = Component;
        }

        return collector;
    }, {});
};

UseFormConfigUtils.buildGroupsByName = ({
    groups = []
} = {}) => groups.reduce((collector, groupConfig) => {

    collector[groupConfig.name] = groupConfig;
    return collector;
}, {});

UseFormConfigUtils.getOwnedFields = (field = {}) => {
    const ownedFields = [];

    if (field.specifyField) {
        ownedFields.push({
            ...field.specifyField,
            render: false,
            ownerFieldName: field.name
        });
    }

    (field.ownedFields || []).forEach((ownedField) => {
        ownedFields.push({
            ...ownedField,
            render: false,
            ownerFieldName: field.name
        });
    });

    return ownedFields;
};

UseFormConfigUtils.getFieldAndOwnedFields = (field = {}) => [
    field,
    ...UseFormConfigUtils.getOwnedFields(field)
];

UseFormConfigUtils.buildDefinitions = ({
    groups = []
} = {}) => groups.reduce((collector, groupConfig) => {

    (groupConfig.fields || []).forEach((field) => {

        UseFormConfigUtils.getFieldAndOwnedFields(field).forEach((fieldConfig) => {

            if (!fieldConfig.name || collector[fieldConfig.name]) {
                return;
            }

            collector[fieldConfig.name] = UseFormConfigUtils.toFieldDefinition({
                field: fieldConfig,
                groupName: groupConfig.name
            });
        });
    });

    return collector;
}, {});

UseFormConfigUtils.buildManagedDefaults = ({
    definitions = {}
} = {}) => {

    const resolvedDefaults = {};
    const resolvingFieldNames = new Set();

    const resolveDefaultValue = (value) => {

        if (!UseFormConfigUtils.isRef(value)) {
            return UseFormConfigUtils.cloneDefaultValue(value);
        }

        const referencedValue = resolveFieldDefault(value.fieldName);

        if (typeof value.path === 'undefined') {
            return UseFormConfigUtils.cloneDefaultValue(referencedValue);
        }

        return UseFormConfigUtils.cloneDefaultValue(UseFormCoreUtils.getPath(referencedValue, value.path));
    };

    const resolveFieldDefault = (fieldName) => {

        if (Object.prototype.hasOwnProperty.call(resolvedDefaults, fieldName)) {
            return resolvedDefaults[fieldName];
        }

        if (resolvingFieldNames.has(fieldName)) {
            throw new Error(`Circular default reference detected for field "${fieldName}".`);
        }

        const definition = definitions[fieldName];

        if (!definition || typeof definition.default === 'undefined') {
            return undefined;
        }

        resolvingFieldNames.add(fieldName);
        resolvedDefaults[fieldName] = resolveDefaultValue(definition.default);
        resolvingFieldNames.delete(fieldName);

        return resolvedDefaults[fieldName];
    };

    Object.values(definitions).forEach((definition) => {

        if (typeof definition.default === 'undefined') {
            return;
        }

        resolveFieldDefault(definition.name);
    });

    return resolvedDefaults;
};

UseFormConfigUtils.buildUiStateDefaults = ({
    definitions = {},
    uiStateDefaults = {}
} = {}) => Object.values(definitions).reduce((collector, definition) => {
    const fieldUiStateDefaults = {
        ...(definition.uiStateDefaults || {})
    };

    if (Object.prototype.hasOwnProperty.call(definition, 'errorShowDefault')) {
        collector[`${definition.name}ErrorShow`] = definition.errorShowDefault;
    }

    if (Object.prototype.hasOwnProperty.call(fieldUiStateDefaults, 'errorShow')) {
        collector[`${definition.name}ErrorShow`] = fieldUiStateDefaults.errorShow;
        delete fieldUiStateDefaults.errorShow;
    }

    return {
        ...collector,
        ...fieldUiStateDefaults
    };
}, {
    ...UseFormStateUtils.buildFormUiStateDefaults(),
    ...uiStateDefaults
});

UseFormConfigUtils.cloneDefaultValue = (value) => {

    if (Array.isArray(value)) {
        return value.slice();
    }

    if (value && typeof value === 'object') {
        return {
            ...value
        };
    }

    return value;
};

UseFormConfigUtils.normalizeDecoratorList = (decorators) => {

    if (!decorators) {
        return [];
    }

    return Array.isArray(decorators) ? decorators : [decorators];
};

UseFormConfigUtils.getFieldDecorators = (config = {}) => UseFormConfigUtils.normalizeDecoratorList(
    config.fieldDecorators || config.decorators?.fields || config.decorators?.field
);

UseFormConfigUtils.getGroupDecorators = (config = {}) => UseFormConfigUtils.normalizeDecoratorList(
    config.groupDecorators || config.decorators?.groups || config.decorators?.group
);

UseFormConfigUtils.applyFieldDecorators = ({
    field,
    group,
    config
}) => UseFormConfigUtils.getFieldDecorators(config).reduce((nextField, decorator) => {

    if (typeof decorator !== 'function') {
        return nextField;
    }

    return decorator(nextField, {
        config,
        group
    }) || nextField;
}, field);

UseFormConfigUtils.applyGroupDecorators = ({
    group,
    config
}) => UseFormConfigUtils.getGroupDecorators(config).reduce((nextGroup, decorator) => {

    if (typeof decorator !== 'function') {
        return nextGroup;
    }

    return decorator(nextGroup, {
        config
    }) || nextGroup;
}, group);

UseFormConfigUtils.applyDecoratorsToGroups = ({
    groups = [],
    config = {}
} = {}) => groups.map((group) => {

    const decoratedGroup = UseFormConfigUtils.applyGroupDecorators({
        group,
        config
    });

    return {
        ...decoratedGroup,
        fields: (decoratedGroup.fields || []).map((field) => UseFormConfigUtils.applyFieldDecorators({
            field,
            group: decoratedGroup,
            config
        }))
    };
});

UseFormConfigUtils.buildManagedFormDefaults = ({
    config = {},
    definitions = {},
    defaults = {},
    props = {},
    helpers = {}
} = {}) => {
    const refs = UseFormConfigUtils.buildReferences({
        config,
        definitions,
        defaults
    });

    return Object.values(definitions).reduce((collector, definition) => {

        const signupDetails = props.signupDetails || {};
        const fieldName = definition.name;
        const defaultValue = defaults[fieldName];
        const builder = definition.getManagedDefault;
        const context = {
            config,
            definition,
            defaults,
            fieldName,
            helpers,
            props,
            refs,
            signupDetails,
            value: signupDetails[fieldName]
        };

        if (typeof builder === 'function') {
            const builtValue = builder(context);

            collector[fieldName] = typeof builtValue === 'undefined'
                ? UseFormConfigUtils.cloneDefaultValue(defaultValue)
                : builtValue;
            return collector;
        }

        if (typeof signupDetails[fieldName] !== 'undefined' && signupDetails[fieldName] !== null) {
            collector[fieldName] = UseFormConfigUtils.cloneDefaultValue(signupDetails[fieldName]);
            return collector;
        }

        collector[fieldName] = UseFormConfigUtils.cloneDefaultValue(defaultValue);
        return collector;
    }, {});
};

UseFormConfigUtils.buildManagedFormDefaultsFromConfig = ({
    config = {},
    props = {},
    helpers = {}
} = {}) => UseFormConfigUtils.buildManagedFormDefaults({
    config,
    definitions: config.definitions,
    defaults: config.managedDefaults,
    props,
    helpers
});

UseFormConfigUtils.buildReferences = ({
    config = {},
    definitions = config.definitions || {},
    defaults = config.managedDefaults || {},
    values = {}
} = {}) => ({
    definition(fieldName) {

        return definitions[fieldName];
    },
    default(fieldName, path) {

        const value = defaults[fieldName];

        if (typeof path === 'undefined') {
            return UseFormConfigUtils.cloneDefaultValue(value);
        }

        return UseFormConfigUtils.cloneDefaultValue(UseFormCoreUtils.getPath(value, path));
    },
    value(fieldName, path) {

        const value = values[fieldName];

        if (typeof path === 'undefined') {
            return UseFormConfigUtils.cloneDefaultValue(value);
        }

        return UseFormConfigUtils.cloneDefaultValue(UseFormCoreUtils.getPath(value, path));
    }
});

UseFormConfigUtils.resolveFieldEffects = ({
    field = {},
    effectName = 'onChangeEffects',
    context = {}
} = {}) => {

    const effects = field[effectName];

    if (!effects) {
        return {};
    }

    const effectList = Array.isArray(effects) ? effects : [effects];
    const matchingEffects = effectList
        .map((effect) => (typeof effect === 'function' ? effect(context) : effect))
        .filter((resolvedEffect) => {

            if (!resolvedEffect) {
                return false;
            }

            if (resolvedEffect.when === 'clear' && context.value !== '') {
                return false;
            }

            if (resolvedEffect.when === 'empty' && context.value) {
                return false;
            }

            if (typeof resolvedEffect.when === 'function' && !resolvedEffect.when(context)) {
                return false;
            }

            return true;
        });

    return matchingEffects.reduce((collector, resolvedEffect) => {
        const updates = typeof resolvedEffect.updates === 'function'
            ? resolvedEffect.updates(context)
            : resolvedEffect.updates;

        if (!updates) {
            return collector;
        }

        return {
            ...collector,
            ...updates
        };
    }, {});
};

UseFormConfigUtils.runFieldEffectSideEffects = ({
    field = {},
    effectName = 'onChangeEffects',
    context = {}
} = {}) => {

    const effects = field[effectName];

    if (!effects) {
        return;
    }

    const effectList = Array.isArray(effects) ? effects : [effects];

    effectList.forEach((effect) => {
        const resolvedEffect = typeof effect === 'function'
            ? effect(context)
            : effect;

        if (!resolvedEffect) {
            return;
        }

        if (resolvedEffect.when === 'clear' && context.value !== '') {
            return;
        }

        if (resolvedEffect.when === 'empty' && context.value) {
            return;
        }

        if (typeof resolvedEffect.when === 'function' && !resolvedEffect.when(context)) {
            return;
        }

        const sideEffects = resolvedEffect.sideEffects || resolvedEffect.sideEffect;
        const sideEffectList = Array.isArray(sideEffects) ? sideEffects : [sideEffects];

        sideEffectList.forEach((sideEffect) => {

            if (typeof sideEffect === 'function') {
                sideEffect(context);
            }
        });
    });
};

UseFormConfigUtils.buildFieldLayoutByGroup = ({
    groups = []
} = {}) => groups.reduce((collector, groupConfig) => {

    if (!groupConfig.fields?.length) {
        return collector;
    }

    collector[groupConfig.name] = groupConfig.fields
        .filter((field) => field.render !== false)
        .map((field) => ({
            name: field.name,
            ...(typeof field.visibleWhen !== 'undefined' ? {
                visibleWhen: field.visibleWhen
            } : {})
        }));

    return collector;
}, {});

UseFormConfigUtils.buildGroupLayout = ({
    groups = []
} = {}) => groups.map((groupConfig) => ({
    name: groupConfig.name,
    fields: (groupConfig.fields || [])
        .filter((field) => field.render !== false)
        .map((field) => ({
            name: field.name,
            ...(typeof field.visibleWhen !== 'undefined' ? {
                visibleWhen: field.visibleWhen
            } : {})
        })),
    ...(typeof groupConfig.visibleWhen !== 'undefined' ? {
        visibleWhen: groupConfig.visibleWhen
    } : {})
}));

UseFormConfigUtils.getFieldDefinition = ({
    definitions = {},
    fieldName,
    defaultDefinition
}) => definitions[fieldName] || defaultDefinition || {
    name: fieldName,
    label: fieldName,
    type: 'TextField'
};

UseFormConfigUtils.getFieldDefinitions = ({
    fieldNames = [],
    getFieldDefinition
}) => fieldNames.map((fieldName) => getFieldDefinition(fieldName));

UseFormConfigUtils.getGroupConfig = ({
    groupsByName = {},
    groupName
}) => groupsByName[groupName] || {
    name: groupName
};

UseFormConfigUtils.getFieldDefinitionsByGroup = ({
    fieldLayout = {},
    getFieldDefinition,
    groupName
}) => (fieldLayout[groupName] || []).map((layoutItem) => ({
    ...getFieldDefinition(layoutItem.name),
    ...layoutItem
}));

UseFormConfigUtils.getFunctionalFieldNames = ({
    fieldLayout = {},
    getFieldDefinition,
    groupName
}) => UseFormConfigUtils.getFieldDefinitionsByGroup({
    fieldLayout,
    getFieldDefinition,
    groupName
})
    .filter((field) => field.type !== 'hidden')
    .map((field) => field.name);

UseFormConfigUtils.buildFormField = ({
    fieldName,
    getFieldDefinition
}) => {

    const definition = getFieldDefinition(fieldName);

    return Object.keys(definition).reduce((collector, key) => {

        if (internals.formFieldExcludedKeys.has(key)) {
            return collector;
        }

        const value = definition[key];

        if (typeof value === 'undefined') {
            return collector;
        }

        collector[key] = key === 'type'
            ? FieldTypeUtils.normalizePrimitiveType(value)
            : value;
        return collector;
    }, {
        type: FieldTypeUtils.normalizePrimitiveType(definition.formType || definition.type || 'TextField'),
        name: definition.name
    });
};

UseFormConfigUtils.buildFormFields = ({
    fieldNames = [],
    getFieldDefinition
}) => fieldNames.map((fieldName) => UseFormConfigUtils.buildFormField({
    fieldName,
    getFieldDefinition
}));

UseFormConfigUtils.buildContext = (props = {}) => ({
    props
});

UseFormConfigUtils.buildInputRefs = ({
    config = {},
    createRef
} = {}) => UseFormCoreUtils.buildNamedRefs({
    createRef,
    names: UseFormConfigUtils.collectRefNames({
        config
    })
});

UseFormConfigUtils.collectRefNames = ({
    config = {}
} = {}) => {

    const refNames = new Set();

    (config.groups || []).forEach((groupConfig) => {

        if (groupConfig.refName) {
            refNames.add(groupConfig.refName);
        }

        (groupConfig.refNames || []).forEach((refName) => {

            refNames.add(refName);
        });

        (groupConfig.fields || []).forEach((field) => {

            UseFormConfigUtils.getFieldAndOwnedFields(field).forEach((fieldConfig) => {

                if (fieldConfig.name) {
                    refNames.add(fieldConfig.name);
                }

                if (fieldConfig.refName) {
                    refNames.add(fieldConfig.refName);
                }

                (fieldConfig.refNames || []).forEach((refName) => {

                    refNames.add(refName);
                });
            });
        });
    });

    return Array.from(refNames);
};

UseFormConfigUtils.buildComponentRegistry = ({
    config = {},
    formId,
    componentsByKey = {},
    componentsByName = {},
    componentsByType = {}
} = {}) => {

    const genericRegistry = UseFormComponentRegistry.getGenericRegistry();
    const registryConfig = config.componentRegistry || {};
    const resolvedComponentsByName = UseFormConfigUtils.buildComponentsByName({
        definitions: config.definitions,
        componentsByName: UseFormConfigUtils.getNamedRegistryComponents({
            componentRegistry: registryConfig
        })
    });
    const resolvedGenericComponentsByType = {
        ...genericRegistry.genericComponentsByType,
        ...(registryConfig.genericComponentsByType || {})
    };
    const resolvedComponentsByType = UseFormConfigUtils.buildComponentsByType({
        definitions: config.definitions,
        componentsByName: resolvedComponentsByName,
        genericComponentsByType: resolvedGenericComponentsByType
    });
    const resolvedComponentsByKey = UseFormConfigUtils.buildComponentsByKey({
        definitions: config.definitions,
        componentsByName: resolvedComponentsByName
    });

    return UseFormComponentRegistry.buildRegistry({
        formId,
        defaultComponent: registryConfig.defaultComponent,
        fieldAdapter: registryConfig.fieldAdapter,
        genericFieldAdapter: registryConfig.genericFieldAdapter,
        genericComponentsByType: resolvedGenericComponentsByType,
        componentsByKey: {
            ...resolvedComponentsByKey,
            ...componentsByKey
        },
        componentsByName: {
            ...resolvedComponentsByName,
            ...componentsByName
        },
        componentsByType: {
            ...resolvedComponentsByType,
            ...componentsByType
        }
    });
};

UseFormConfigUtils.buildGroups = ({
    config = {},
    overrides = {},
    layout = config.groupLayout || [],
    context,
    componentRegistry = {},
    getFieldDefinition
} = {}) => {
    const fieldLayout = config.fieldLayout || {};
    const conditionTypes = {
        ...UseFormCoreUtils.conditionTypes,
        ...(config.conditionTypes || {})
    };
    const groups = (config.groups || []).map((groupConfig) => {
        const override = overrides[groupConfig.name] || {};
        const functionalFieldNames = UseFormConfigUtils.getFunctionalFieldNames({
            fieldLayout,
            getFieldDefinition,
            groupName: groupConfig.name
        });

        return {
            name: groupConfig.name,
            type: override.type || groupConfig.type,
            component: override.component || groupConfig.component,
            componentProps: {
                ...componentRegistry,
                ...internals.buildGroupConfigComponentProps(groupConfig),
                ...groupConfig.componentProps,
                ...override.componentProps,
                ...UseFormGroupUtils.buildGroupComponentProps({
                    context,
                    fieldNames: functionalFieldNames,
                    layout: fieldLayout[groupConfig.name] || [],
                    conditionTypes,
                    getFieldDefinition
                })
            },
            functionalFieldNames,
            methods: override.methods || []
        };
    });

    return UseFormLayoutUtils.applyLayoutToGroups({
        groups,
        layout,
        context,
        conditionTypes
    });
};

UseFormConfigUtils.buildResolvedConfig = ({
    config = {},
    props = {},
    groupOverrides,
    groupLayout = config.groupLayout || [],
    formId = props.formId || config.id,
    componentsByKey = props.componentsByKey,
    componentsByName = props.componentsByName,
    componentsByType = props.componentsByType,
    getFieldDefinition
} = {}) => {

    const context = UseFormConfigUtils.buildContext(props);
    const componentRegistry = UseFormConfigUtils.buildComponentRegistry({
        config,
        formId,
        componentsByKey,
        componentsByName,
        componentsByType
    });
    const groups = UseFormConfigUtils.buildGroups({
        config,
        overrides: groupOverrides,
        layout: groupLayout,
        context,
        componentRegistry,
        getFieldDefinition
    });

    return {
        ...config,
        context,
        groupLayout,
        groups,
        componentRegistry
    };
};

UseFormConfigUtils.finalizeConfig = (config = {}) => {

    const finalizedConfig = {
        ...config
    };

    finalizedConfig.groups = UseFormConfigUtils.applyDecoratorsToGroups({
        groups: finalizedConfig.groups || [],
        config: finalizedConfig
    });
    finalizedConfig.groupsByName = UseFormConfigUtils.buildGroupsByName({
        groups: finalizedConfig.groups
    });
    finalizedConfig.definitions = UseFormConfigUtils.buildDefinitions({
        groups: finalizedConfig.groups
    });
    finalizedConfig.fieldLayout = UseFormConfigUtils.buildFieldLayoutByGroup({
        groups: finalizedConfig.groups
    });
    finalizedConfig.groupLayout = finalizedConfig.groupLayout || UseFormConfigUtils.buildGroupLayout({
        groups: finalizedConfig.groups
    });
    finalizedConfig.managedDefaults = UseFormConfigUtils.buildManagedDefaults({
        definitions: finalizedConfig.definitions
    });
    finalizedConfig.getFieldDefinition = (fieldName) => UseFormConfigUtils.getFieldDefinition({
        definitions: finalizedConfig.definitions,
        fieldName
    });
    finalizedConfig.uiStateDefaults = UseFormConfigUtils.buildUiStateDefaults({
        definitions: finalizedConfig.definitions,
        uiStateDefaults: finalizedConfig.uiStateDefaults
    });
    finalizedConfig.managedFieldNames = Object.keys(finalizedConfig.managedDefaults);
    finalizedConfig.getFieldDefinitions = (fieldNames = []) => UseFormConfigUtils.getFieldDefinitions({
        fieldNames,
        getFieldDefinition: finalizedConfig.getFieldDefinition
    });
    finalizedConfig.getGroupConfig = (groupName) => UseFormConfigUtils.getGroupConfig({
        groupsByName: finalizedConfig.groupsByName,
        groupName
    });
    finalizedConfig.getFieldDefinitionsByGroup = (groupName) => UseFormConfigUtils.getFieldDefinitionsByGroup({
        fieldLayout: finalizedConfig.fieldLayout,
        getFieldDefinition: finalizedConfig.getFieldDefinition,
        groupName
    });
    finalizedConfig.getFunctionalFieldNames = (groupName) => UseFormConfigUtils.getFunctionalFieldNames({
        fieldLayout: finalizedConfig.fieldLayout,
        getFieldDefinition: finalizedConfig.getFieldDefinition,
        groupName
    });
    finalizedConfig.buildFormField = (fieldName) => UseFormConfigUtils.buildFormField({
        fieldName,
        getFieldDefinition: finalizedConfig.getFieldDefinition
    });
    finalizedConfig.buildFormFields = (fieldNames = []) => UseFormConfigUtils.buildFormFields({
        fieldNames,
        getFieldDefinition: finalizedConfig.getFieldDefinition
    });
    finalizedConfig.formFields = finalizedConfig.buildFormFields(finalizedConfig.managedFieldNames);
    finalizedConfig.buildManagedFormDefaults = ({
        props = {},
        helpers = {}
    } = {}) => UseFormConfigUtils.buildManagedFormDefaultsFromConfig({
        config: finalizedConfig,
        props,
        helpers
    });
    finalizedConfig.buildInputRefs = (createRef) => UseFormConfigUtils.buildInputRefs({
        config: finalizedConfig,
        createRef
    });
    finalizedConfig.buildReferences = ({
        defaults = finalizedConfig.managedDefaults,
        values = {}
    } = {}) => UseFormConfigUtils.buildReferences({
        config: finalizedConfig,
        definitions: finalizedConfig.definitions,
        defaults,
        values
    });
    finalizedConfig.buildComponentRegistry = ({
        formId,
        componentsByKey = {},
        componentsByName = {},
        componentsByType = {}
    } = {}) => UseFormConfigUtils.buildComponentRegistry({
        config: finalizedConfig,
        formId,
        componentsByKey,
        componentsByName,
        componentsByType
    });
    finalizedConfig.buildGroups = ({
        overrides = {},
        layout = finalizedConfig.groupLayout,
        context,
        componentRegistry = {}
    } = {}) => UseFormConfigUtils.buildGroups({
        config: finalizedConfig,
        overrides,
        layout,
        context,
        componentRegistry,
        getFieldDefinition: finalizedConfig.getFieldDefinition
    });
    finalizedConfig.buildConfig = ({
        props = {},
        groupOverrides,
        groupLayout = finalizedConfig.groupLayout,
        formId = props.formId || finalizedConfig.id,
        componentsByKey = props.componentsByKey,
        componentsByName = props.componentsByName,
        componentsByType = props.componentsByType
    } = {}) => UseFormConfigUtils.buildResolvedConfig({
        config: finalizedConfig,
        props,
        groupOverrides,
        groupLayout,
        formId,
        componentsByKey,
        componentsByName,
        componentsByType,
        getFieldDefinition: finalizedConfig.getFieldDefinition
    });
    finalizedConfig.buildFormConfig = finalizedConfig.buildConfig;

    return finalizedConfig;
};

UseFormConfigUtils.internals = internals;
